import React, { useEffect, useState } from "react";
import s from "./PromoCode.module.scss";
import clsx from "clsx";
import dayjs from "dayjs";
import Paginate from "../../../../global components/Paginate/Pagination";
import { Skeleton } from "antd";
import ReusableTable from "../../../../global components/Table/Table";
import NoData from "../../../../global components/Table/NoData/NoData";
import SelectField from "../../../../global components/Forms/SelectField/SelectField";
import SearchInput from "../../../../global components/Forms/SearchInput/SearchInput";
import ReusablePopover from "../../../../global components/Popover/ReuseablePopover";
import PopOverItems from "./Local Components/PopOverItems/PopOverItems";
import Elipses from "../../../../global components/Icons/Elipses";
import Header from "./Local Components/Header/Header";
import Card from "./Local Components/Card/Card";
import PromoCodeIcon from "../../../../global components/Icons/PromoCode/PromoCode";
import { UseAuthToken } from "../../../../libs/api/userSession";
import useSWR from "swr";
import { parseClientError, swrFetcher } from "../../../../libs/api/Endpoints";
import { PromoCodeResponse } from "../../../../libs/Utils/Types/PromoCode";
import Unauthorised from "../../../../global components/Unauthorised";
import CsvDownloader from "./Local Components/CsvDownloader";

type promoStats = {
  total: number;
  active: number;
  inactive: number;
  expired: number;
  totalUsageSum: number;
};
function PromoCode() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [filter, setFilter] = useState<string>("");
  const token = UseAuthToken();
  const [unauthorised, setUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const [tableUnauthorised, setTablwUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const {
    data: promocode,
    isLoading,
    error,
    mutate,
  } = useSWR<PromoCodeResponse, any, any>(
    token
      ? [
          "api/admin/promo-codes",
          token,
          {
            page: currentPage,
            ...(filter && { status: filter }),
            search: searchValue,
          },
        ]
      : null,
    swrFetcher,
    { retryOnError: false, revalidateOnFocus: true },
  );
  const {
    data: promocodeStats,
    isLoading: loading,
    error: statError,
  } = useSWR<{ data: promoStats }, any, any>(
    token
      ? [
          "api/admin/promo-codes/stats",
          token,
          { page: currentPage, status: filter, search: searchValue },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true,
      retryOnError: false,
    },
  );
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // Reset to the first page
  };

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  function handleFilter(value: string) {
    setFilter(value);
  }
  // Define table columns
  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string) => (
        <span className={s.credit}>{description}</span>
      ),
    },

    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code: string) => <span className={s.amount}>{code}</span>,
    },
    {
      title: "Usage limit",
      dataIndex: "usesageLimit",
      key: "usesageLimit",
      render: (usesageLimit: string) => (
        <span className={s.credit}>{usesageLimit}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: string) => <span className={s.amount}>{amount}</span>,
    },
    {
      title: "Start date",
      dataIndex: "startDate",
      key: "startDate",
      render: (startDate: string) => (
        <span className={s.date}>
          {dayjs.utc(startDate).local().format("MMM D, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "End date",
      dataIndex: "endDate",
      key: "endDate",
      render: (endDate: string) => (
        <span className={s.date}>
          {dayjs.utc(endDate).local().format("MMM D, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Recipients",
      dataIndex: "recipients",
      key: "recipients",
      render: (recipients: string) => (
        <span className={s.date}>{recipients}</span>
      ),
    },
    {
      title: "Redemption",
      dataIndex: "redemption",
      key: "redemption",
      render: (redemption: string) => (
        <span className={s.date}>{redemption}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={clsx(
            s.status,
            s.statusCell,
            s[
              status === "inactive"
                ? "pending"
                : status === "active"
                  ? "successful"
                  : "failed"
            ],
          )}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (
        text: any,
        record: { key: string; code: string; status: string },
      ) => (
        <ReusablePopover
          trigger={"click"}
          placement="rightBottom"
          content={
            <PopOverItems
              code={record.code}
              mutate={mutate}
              status={record.status}
              keyValue={record.key}
            />
          } // Pass the key value
        >
          <Elipses height={24} width={18} />
        </ReusablePopover>
      ),
    },
  ];
  const transformedData = promocode?.data.map((code) => ({
    key: code.id,
    description: code.description || "N/A",
    code: code.name || "N/A",
    usesageLimit: code.usageLimit || "N/A",
    amount:
      code.type === "percentage"
        ? code.creditPercentage?.toString() + "%" || "N/A"
        : code.maxCredit || "N/A",
    startDate: code.startDate || "N/A",
    recipients: code.recipientCount || 0,
    endDate: code.endDate,
    redemption: code.totalUsage,
    status: code.status || "N/A",
    actions: "",
  }));
  useEffect(() => {
    if (error) {
      const e = parseClientError(error);
      if (e.statusCode === 403) {
        setUnauthorised(true);
      } else {
        setUnauthorised({
          title: "Error",
          message:
            e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
            "Something went wrong",
        });
      }
    }
  }, [error]);

  useEffect(() => {
    if (statError) {
      const e = parseClientError(statError);

      if (e.statusCode === 403) {
        setTablwUnauthorised(true);
      } else {
        setTablwUnauthorised({
          title: "Error",
          message:
            e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
            "Something went wrong",
        });
      }
    }
  }, [statError]);
  return (
    <div className={s.wrapper}>
      <Header
        unauthorized={typeof unauthorised === "boolean" && unauthorised}
      />
      <div className={s.cards}>
        {unauthorised ? (
          typeof unauthorised === "boolean" ? (
            <div className={s.cardError}>
              <Unauthorised />
            </div>
          ) : (
            <div className={s.cardError}>
              <Unauthorised error={unauthorised} />
            </div>
          )
        ) : (
          <>
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => setFilter("")}
                label="Total code"
                stat={promocodeStats?.data.total || 0}
              />
            )}
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => setFilter("active")}
                label="Active"
                stat={promocodeStats?.data.active || 0}
              />
            )}
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => setFilter("inactive")}
                label="Inactive"
                stat={promocodeStats?.data.inactive || 0}
              />
            )}
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => setFilter("expired")}
                label="Expired"
                stat={promocodeStats?.data.expired || 0}
              />
            )}
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                label="Total redemption"
                stat={promocodeStats?.data.totalUsageSum || 0}
              />
            )}
          </>
        )}
      </div>
      <div className={s.table}>
        <div className={s.searchParams}>
          <div className={s.params}>
            <div className={s.searchInput}>
              <SearchInput
                placeholder="search ID"
                handleSearch={handleSearch}
              />
            </div>
            <SelectField
              bordered
              label={"Status:"}
              wrapperClassname={s.SelectWrapper}
              defaultValue={""}
              value={filter}
              onChange={(value: any) => handleFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "expired", label: "Expired" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
          </div>
          <div className={s.params}>
            <div className={s.paginate}>
              <Paginate
                onChange={handlePageChange}
                total={promocode?.pagination.totalItems || 0}
                pageSize={pageItems}
              />
            </div>
            <CsvDownloader
              token={token!}
              filter={filter}
              endpointKey="api/admin/promo-codes"
              filename="Promo-code"
              pageItems={pageItems}
            />
          </div>
        </div>

        <ReusableTable
          loading={isLoading}
          noDataComponent={
            tableUnauthorised ? (
              typeof tableUnauthorised === "boolean" ? (
                <Unauthorised />
              ) : (
                <Unauthorised error={tableUnauthorised} />
              )
            ) : (
              <NoData
                Message="No promo codes yet"
                subMessage="Get started by creating a promo code"
                icon={<PromoCodeIcon height={80} width={80} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
        />
      </div>
    </div>
  );
}

export default PromoCode;
